/**
 * Referee Withdrawal Contracts
 *
 * DATABASE-FIRST: Matches create_referee_withdrawal() function + withdrawals table
 * Source: MCP query on 2026-01-02, updated 2026-01-11 (migration 033 added user_telegram_id)
 *
 * Function signature:
 *   create_referee_withdrawal(p_referee_identifier varchar, p_amount numeric, p_card_number varchar, p_user_telegram_id bigint)
 *   RETURNS TABLE(success boolean, message text, reference_number varchar)
 *
 * Note: p_user_telegram_id is the referee's telegram ID (from session), needed for FK constraint.
 * The API client only sends amount + cardNumber; user_telegram_id comes from auth.
 *
 * Referee withdrawals use prefix REF- and are stored in withdrawals table
 * with referee_identifier column populated (NULL for user withdrawals)
 */
import { z } from 'zod';
import { withdrawalStatusSchema, successResponseSchema, timestampStringRequired, timestampString, } from '../common.js';
// =============================================================================
// REQUEST SCHEMA
// =============================================================================
/**
 * POST /api/referee/withdrawal request body
 * Matches backend WithdrawalRequestSchema
 */
export const refereeWithdrawalRequestSchema = z.object({
    amount: z.number().positive().max(100000),
    cardNumber: z.string().min(13).max(19),
});
// =============================================================================
// STORED PROCEDURE RESULT
// =============================================================================
/**
 * create_referee_withdrawal() return type
 */
export const createRefereeWithdrawalResultSchema = z.object({
    success: z.boolean(),
    message: z.string(),
    reference_number: z.string().nullable(), // Only present on success
});
// =============================================================================
// API RESPONSES
// =============================================================================
/**
 * POST /api/referee/withdrawal response data (inner data, for frontend consumption)
 */
export const createRefereeWithdrawalResponseDataSchema = z.object({
    message: z.string(),
    referenceNumber: z.string(),
    amount: z.number(),
});
/**
 * POST /api/referee/withdrawal full response (with success wrapper)
 */
export const createRefereeWithdrawalResponseSchema = successResponseSchema(createRefereeWithdrawalResponseDataSchema);
/**
 * Single referee withdrawal entry (from withdrawals table)
 * Used in GET /api/referee/withdrawals list
 */
export const refereeWithdrawalSchema = z.object({
    referenceNumber: z.string(),
    amount: z.number(), // original_amount column
    finalAmount: z.number(), // amount column (after any edits)
    status: withdrawalStatusSchema,
    cardNumber: z.string().nullable(),
    createdAt: timestampStringRequired,
    completedAt: timestampString,
});
/**
 * GET /api/referee/withdrawals response data (inner data, for frontend consumption)
 */
export const refereeWithdrawalsResponseDataSchema = z.object({
    withdrawals: z.array(refereeWithdrawalSchema),
    total: z.number().int().nonnegative(),
});
/**
 * GET /api/referee/withdrawals full response (with success wrapper)
 */
export const refereeWithdrawalsResponseSchema = successResponseSchema(refereeWithdrawalsResponseDataSchema);
// =============================================================================
// QUERY PARAMS
// =============================================================================
/**
 * Withdrawals list query parameters
 */
export const refereeWithdrawalsQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().nonnegative().default(0),
});
//# sourceMappingURL=withdrawal.js.map